import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import MessagePropType from '../definitions/MessagePropType';
import Colours from '../definitions/Colours';
import ZIndex from '../definitions/ZIndex';
import Sizes from '../definitions/Sizes';
import Tooltip, {
    LEFT,
    RIGHT,
    TOP,
    BOTTOM,

    START,
    MIDDLE,
    END,

    ALIGNMENT_OPTIONS,
    LOCATION_OPTIONS,
} from './Tooltip';
import QuestionMarkButton from './QuestionMarkButton';


function TooltipAnchor({
    message,
    tooltipLocation = RIGHT,
    nubbinAlignment = START,
    tooltipWidth = '12em',
    buttonSize,
    'data-name': dataName = 'tooltip-anchor', // TODO: This should be required; but make sure we have a data-name prop because it breaks without it
    className,
    onTooltipClick,
    children,
    isCompact = false,
    ...otherProps
}) {
    const [ isTooltipOpen, setIsTooltipOpen ] = useState(false);

    // Add or remove a click/keypress handler to the root of the document body,
    // so a click anywhere will close the tooltip.
    useEffect(() => {
        // If tooltip is not open, we have nothing to do.
        if (!isTooltipOpen) {
            return;
        }

        function handleBodyClick(e) {
            if (e.target.closest(`[data-name="${dataName}"]`)) {
                // User is clicking the button, so don't handle that click here.
                e.stopPropagation();
                return false;
            }

            if (e.target.closest(`.${TOOLTIP_CLASSNAME}`)) {
                // User is clicking inside a tooltip, so don't close it.
                // They are likely trying to select text or interact with
                // links in the tooltip.
                e.stopPropagation();
                return false;
            }

            setIsTooltipOpen(false);
        }

        function handleBodyKeyUp(e) {
            // Allow closing the tooltip by pressing esc or "tabbing away" to
            // something outside the tooltip.
            //
            // Intentionally using keyboard event and not blur event so this
            // will work in Safari as well, which by default cannot focus/blur
            // on buttons.
            if (
                e.keyCode === KEYCODE_ESC
                || (e.keyCode === KEYCODE_TAB && !e.target.closest(`.${TOOLTIP_CLASSNAME}`))
            ) {
                setIsTooltipOpen(false);
            }
        }

        // We're relying on events bubbling to the document body, so we can
        // close the tooltip if they click outside the tooltip.
        document.body.addEventListener('click', handleBodyClick);
        document.body.addEventListener('keyup', handleBodyKeyUp);

        return () => {
            document.body.removeEventListener('click', handleBodyClick);
            document.body.removeEventListener('keyup', handleBodyKeyUp);
        };
    }, [ dataName, isTooltipOpen ]);

    function handleButtonClick() {
        setIsTooltipOpen(!isTooltipOpen);
        onTooltipClick?.();
    }

    return (
        <Container
            tooltipLocation={tooltipLocation}
            nubbinAlignment={nubbinAlignment}
            className={className}
            isTooltipVisible={isTooltipOpen}
        >
            <QuestionMarkButton
                iconColour={Colours.BLACKBERRY}
                onClick={handleButtonClick}
                data-name={dataName}
                isFocused={isTooltipOpen}
                buttonSize={buttonSize}
            />

            <Tooltip
                className={TOOLTIP_CLASSNAME}
                message={message}
                nubbinLocation={getNubbinLocation(tooltipLocation)}
                nubbinAlignment={nubbinAlignment}
                width={tooltipWidth}
                data-test-name="tooltip-content"
                isCompact={isCompact}
                {...otherProps}
            >
                {children}
            </Tooltip>
        </Container>
    );
}

TooltipAnchor.propTypes = {
    message: MessagePropType,
    tooltipLocation: PropTypes.oneOf(LOCATION_OPTIONS),
    nubbinAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),
    tooltipWidth: PropTypes.string,
    buttonSize: PropTypes.oneOf(Object.values(Sizes.ICONS)),
    className: PropTypes.string,
    'data-name': PropTypes.string, // TODO: This should be required
    onTooltipClick: PropTypes.func,
    children: PropTypes.node,
    isCompact: PropTypes.bool,
};

const TOOLTIP_CLASSNAME = 'tooltip-component';
const KEYCODE_ESC = 27;
const KEYCODE_TAB = 9;
const ANCHOR_TOOLTIP_SIZE = '1.25em';
const TRANSITION_DURATION = '300ms';

const Container = styled.div`
    position: relative;

    > .${TOOLTIP_CLASSNAME} {
        position: absolute;
        text-align: left;
        color: ${Colours.BLACKBERRY};
        z-index: ${ZIndex.NOTIFICATIONS};

        visibility: hidden;
        opacity: 0;

        transition:
            opacity ${TRANSITION_DURATION},
            visibility ${TRANSITION_DURATION};

        ${props => props.tooltipLocation === RIGHT && `
            left: calc(100% + ${ANCHOR_TOOLTIP_SIZE});
        `}

        ${props => props.tooltipLocation === LEFT && `
            right: calc(100% + ${ANCHOR_TOOLTIP_SIZE});
        `}

        ${props => (props.tooltipLocation === LEFT || props.tooltipLocation === RIGHT)
            && props.nubbinAlignment === START && `
            top: -1em;
        `}

        ${props => (props.tooltipLocation === LEFT || props.tooltipLocation === RIGHT)
            && props.nubbinAlignment === MIDDLE && `
            bottom: 50%;
            transform: translateY(50%);
        `}

        ${props => (props.tooltipLocation === LEFT || props.tooltipLocation === RIGHT)
            && props.nubbinAlignment === END && `
            bottom: -1.25em;
        `}

        ${props => props.tooltipLocation === BOTTOM && `
            top: calc(100% + ${ANCHOR_TOOLTIP_SIZE});
        `}

        ${props => props.tooltipLocation === TOP && `
            bottom: calc(100% + ${ANCHOR_TOOLTIP_SIZE});
        `}

        ${props => (props.tooltipLocation === TOP || props.tooltipLocation === BOTTOM)
            && props.nubbinAlignment === START && `
            left: -1em;
        `}

        ${props => (props.tooltipLocation === TOP || props.tooltipLocation === BOTTOM)
            && props.nubbinAlignment === MIDDLE && `
            right: 50%;
            transform: translateX(50%);
        `}

        ${props => (props.tooltipLocation === TOP || props.tooltipLocation === BOTTOM)
            && props.nubbinAlignment === END && `
            right: 0.15em;
        `}
    }

    ${props => props.isTooltipVisible && `
        outline: none;

        > .${TOOLTIP_CLASSNAME} {
            visibility: visible;
            opacity: 1;
        }
    `}
`;

const TOOLTIP_LOCATION_TO_NUBBIN_LOCATION_MAP = {
    [LEFT]: RIGHT,
    [RIGHT]: LEFT,
    [TOP]: BOTTOM,
    [BOTTOM]: TOP,
};

function getNubbinLocation(tooltipLocation) {
    return TOOLTIP_LOCATION_TO_NUBBIN_LOCATION_MAP[tooltipLocation];
}

export default TooltipAnchor;
